import { useEffect, useRef } from "react";
import { FaTimes } from "react-icons/fa";
import { usePubSub, useMeeting } from "@videosdk.live/react-sdk";
import { useSiteContext } from "../../context/SiteContext";
import logger from '../../utils/logger';

export default function DocumentViewer({ fileUrl, onClosePanel }) {
  const viewer = useRef(null);
  const webviewerInstanceRef = useRef(null);
  const isApplyingRemoteChange = useRef(false);
  const publishRef = useRef(null);
  const { meetingRole } = useSiteContext();
  const mMeeting = useMeeting();

  const { publish } = usePubSub("SHOW_DOCUMENT_SECTION", {
    onMessageReceived: (data) => {
      const instance = webviewerInstanceRef.current;
      if (!instance || !data?.message) return;

      try {
        const messageData =
          typeof data.message === "string"
            ? JSON.parse(data.message)
            : data.message;

        // Don't process our own events
        if (data.senderId === mMeeting?.localParticipant?.id) {
          return;
        }

        const { documentViewer } = instance.Core;
        const { eventName, eventData } = messageData;

        switch (eventName) {
          case "pageNumberUpdated":
            if (
              eventData?.pageNumber &&
              documentViewer.getCurrentPage() !== eventData.pageNumber
            ) {
              // Set flag to prevent feedback loop
              isApplyingRemoteChange.current = true;
              documentViewer.setCurrentPage(eventData.pageNumber);
              // Reset flag after navigation completes
              setTimeout(() => {
                isApplyingRemoteChange.current = false;
              }, 600);
            }
            break;
          default:
            logger("log", "Unknown event:", eventName);
        }
      } catch (error) {
        logger("error", "Error handling document event:", error);
      }
    },
  });

  // Store publish function in ref to avoid stale closures
  useEffect(() => {
    publishRef.current = publish;
  }, [publish]);

  const sanitizeUrl = (url) => {
    if (url.startsWith("http://")) {
      return url.replace("http://", "https://");
    } else if (url.startsWith("https://")) {
      return url;
    } else {
      return `https://${url}`;
    }
  };

  useEffect(() => {
    let cleanupFunctions = [];

    import("@pdftron/webviewer").then((module) => {
      const WebViewer = module.default;
      WebViewer(
        {
          path: "/lib/webviewer",
          initialDoc: sanitizeUrl(fileUrl),
          licenseKey:
            "R-Link LLC:OEM:a5jOK000000xArN::B+3:AMS(20260828):A773575F761D1145BB535788D2C8F496839C21EDF969F948792B170966FA656D",
        },
        viewer.current
      ).then((instance) => {
        const { UI } = instance;
        UI.setLayoutMode(UI.LayoutMode.Single);

        // Disable UI elements but keep page navigation
        UI.disableElements([
          "documentHeader",
          "header",
          "toolsHeader",
          "toolbar",
          "ribbon",
          "ribbons",
          "toolbarGroup-Edit",
          "toolbarGroup-Annotate",
          "toolbarGroup-Shapes",
          "toolbarGroup-Insert",
          "toolbarGroup-Forms",
          "toolbarGroup-Comment",
          "toolbarGroup-Print",
          "toolbarGroup-Download",
          "toolbarGroup-FillAndSign",
          "toolbarGroup-View",
          "toolbarGroup-Measure",
          "toolbarGroup-Zoom",
          "snippingToolButton",
          "cropToolButton",
          "panToolButton",
          "selectToolButton",
          "viewControlsTab",
          "selectButton",
          "menuButton",
          "toolsButton",
          // Keep Navigation and Page controls enabled - explicitly enable them
        ]);

        // Explicitly enable pagination controls
        try {
          UI.enableElements([
            "pageNavigation",
            "previousPageButton",
            "nextPageButton",
            "pageNumberInput",
            "pageNumberDisplay",
          ]);
        } catch (e) {
          logger("log", "Could not enable pagination elements:", e);
        }

        // Hide toolbar completely but keep pagination visible
        try {
          // Method 1: Set header items to empty
          if (UI.setHeaderItems) {
            UI.setHeaderItems(() => []);
          }
        } catch (e) {
          logger("log", "setHeaderItems not available");
        }

        try {
          // Method 2: Try to get and hide header element directly
          const headerElement = UI.getHeader();
          if (headerElement) {
            headerElement.style.display = "none";
            headerElement.style.visibility = "hidden";
            headerElement.style.height = "0";
          }
        } catch (e) {
          logger("log", "getHeader not available");
        }

        try {
          // Method 3: Try to disable the entire UI
          if (UI.disableFeatures) {
            UI.disableFeatures(["header"]);
          }
        } catch (e) {
          logger("log", "disableFeatures not available");
        }

        // Inject CSS to hide toolbar but preserve pagination
        const styleId = "document-viewer-hide-toolbar-keep-pagination";
        if (!document.getElementById(styleId)) {
          const style = document.createElement("style");
          style.id = styleId;
          style.textContent = `
            /* Hide all toolbar elements */
            [data-element="header"],
            [data-element="toolsHeader"],
            [data-element="menuButton"],
            [data-element="ribbons"],
            [data-element*="toolbar"]:not([data-element*="navigation"]):not([data-element*="page"]):not([data-element*="Page"]),
            [data-element*="header"]:not([data-element*="navigation"]):not([data-element*="page"]):not([data-element*="Page"]),
            [data-element*="menu"]:not([data-element*="navigation"]):not([data-element*="page"]):not([data-element*="Page"]),
            .Header:not([class*="navigation"]):not([class*="page"]):not([class*="Navigation"]):not([class*="Page"]),
            .ToolsHeader:not([class*="navigation"]):not([class*="page"]):not([class*="Navigation"]):not([class*="Page"]),
            .Menu:not([class*="navigation"]):not([class*="page"]):not([class*="Navigation"]):not([class*="Page"]),
            .Toolbar:not([class*="navigation"]):not([class*="page"]):not([class*="Navigation"]):not([class*="Page"]),
            .Ribbons:not([class*="navigation"]):not([class*="page"]):not([class*="Navigation"]):not([class*="Page"]),
            [class*="Header"]:not([class*="navigation"]):not([class*="page"]):not([class*="Navigation"]):not([class*="Page"]),
            [class*="Toolbar"]:not([class*="navigation"]):not([class*="page"]):not([class*="Navigation"]):not([class*="Page"]),
            [class*="Menu"]:not([class*="navigation"]):not([class*="page"]):not([class*="Navigation"]):not([class*="Page"]),
            [class*="header"]:not([class*="navigation"]):not([class*="page"]):not([class*="Navigation"]):not([class*="Page"]),
            [class*="toolbar"]:not([class*="navigation"]):not([class*="page"]):not([class*="Navigation"]):not([class*="Page"]),
            [class*="menu"]:not([class*="navigation"]):not([class*="page"]):not([class*="Navigation"]):not([class*="Page"]),
            [class*="ribbon"]:not([class*="navigation"]):not([class*="page"]):not([class*="Navigation"]):not([class*="Page"]) {
              display: none !important;
              visibility: hidden !important;
              height: 0 !important;
              overflow: hidden !important;
            }
            
            /* Explicitly show pagination controls */
            [data-element*="navigation"],
            [data-element*="page"],
            [data-element*="Page"],
            [data-element*="previousPage"],
            [data-element*="nextPage"],
            [data-element*="pageNumber"],
            [class*="navigation"],
            [class*="pageNavigation"],
            [class*="PageNavigation"],
            [class*="page-control"],
            [class*="PageControl"] {
              display: flex !important;
              visibility: visible !important;
              height: auto !important;
              overflow: visible !important;
            }
          `;
          document.head.appendChild(style);
          cleanupFunctions.push(() => {
            const styleEl = document.getElementById(styleId);
            if (styleEl) {
              document.head.removeChild(styleEl);
            }
          });
        }

        // Function to hide toolbar elements but preserve pagination
        const hideToolbarElements = () => {
          const viewerElement = viewer.current;
          if (!viewerElement) return;

          // Hide all toolbar elements but NOT navigation/page controls
          const allElements = viewerElement.querySelectorAll("*");
          allElements.forEach((el) => {
            const dataElement = el.getAttribute("data-element") || "";
            const className = el.className?.toString() || "";
            const id = el.id || "";
            const tagName = el.tagName?.toLowerCase() || "";

            // Check if it's a navigation/pagination element - don't hide those
            const isNavigation =
              dataElement.toLowerCase().includes("navigation") ||
              dataElement.toLowerCase().includes("pagecontrol") ||
              dataElement.toLowerCase().includes("page-control") ||
              dataElement.toLowerCase().includes("previouspage") ||
              dataElement.toLowerCase().includes("nextpage") ||
              dataElement.toLowerCase().includes("pagenumber") ||
              dataElement.toLowerCase().includes("pageNumberInput") ||
              dataElement.toLowerCase().includes("pageNumberDisplay") ||
              className.toLowerCase().includes("navigation") ||
              className.toLowerCase().includes("pagecontrol") ||
              className.toLowerCase().includes("page-control") ||
              className.toLowerCase().includes("previouspage") ||
              className.toLowerCase().includes("nextpage") ||
              className.toLowerCase().includes("pagenumber") ||
              className.toLowerCase().includes("pagenavigation") ||
              id.toLowerCase().includes("navigation") ||
              id.toLowerCase().includes("pagecontrol") ||
              id.toLowerCase().includes("page-control") ||
              id.toLowerCase().includes("pagenavigation");

            // Hide toolbar elements but not navigation - also ensure pagination stays visible
            if (
              !isNavigation &&
              (dataElement.toLowerCase().includes("header") ||
                dataElement.toLowerCase().includes("toolbar") ||
                dataElement.toLowerCase().includes("menu") ||
                dataElement.toLowerCase().includes("ribbon") ||
                className.toLowerCase().includes("header") ||
                className.toLowerCase().includes("toolbar") ||
                className.toLowerCase().includes("menu") ||
                className.toLowerCase().includes("ribbon") ||
                id.toLowerCase().includes("header") ||
                id.toLowerCase().includes("toolbar") ||
                id.toLowerCase().includes("menu") ||
                tagName === "header")
            ) {
              el.style.display = "none";
              el.style.visibility = "hidden";
              el.style.height = "0";
              el.style.overflow = "hidden";
            } else if (isNavigation) {
              // Explicitly ensure pagination elements are visible
              el.style.display = "";
              el.style.visibility = "visible";
              el.style.height = "";
              el.style.overflow = "";
            }
          });
        };

        // Use MutationObserver to catch dynamically added toolbar elements
        const observer = new MutationObserver(() => {
          hideToolbarElements();
        });

        if (viewer.current) {
          observer.observe(viewer.current, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["class", "data-element"],
          });
          cleanupFunctions.push(() => {
            observer.disconnect();
          });
        }

        // Hide toolbars after multiple delays
        setTimeout(hideToolbarElements, 100);
        setTimeout(hideToolbarElements, 300);
        setTimeout(hideToolbarElements, 500);
        setTimeout(hideToolbarElements, 1000);
        setTimeout(hideToolbarElements, 2000);

        const { documentViewer } = instance.Core;

        // Disable page navigation for non-moderators
        if (meetingRole !== "moderator") {
          // Disable keyboard navigation
          const handleKeyDown = (e) => {
            // Prevent arrow keys, page up/down, home/end
            if (
              [
                "ArrowUp",
                "ArrowDown",
                "ArrowLeft",
                "ArrowRight",
                "PageUp",
                "PageDown",
                "Home",
                "End",
              ].includes(e.key)
            ) {
              e.preventDefault();
              e.stopPropagation();
            }
          };

          // Disable UI navigation elements
          try {
            UI.disableElements([
              "previousPageButton",
              "nextPageButton",
              "pageNavigation",
            ]);
          } catch (e) {
            logger("log", "Could not disable navigation elements:", e);
          }

          // Prevent programmatic page changes
          const originalSetCurrentPage = documentViewer.setCurrentPage;
          documentViewer.setCurrentPage = function (pageNumber) {
            // Only allow if it's a remote change
            if (isApplyingRemoteChange.current) {
              return originalSetCurrentPage.call(this, pageNumber);
            }
            // Block manual page changes
            return false;
          };

          // Add keyboard listener
          document.addEventListener("keydown", handleKeyDown);

          // Store cleanup function
          cleanupFunctions.push(() => {
            document.removeEventListener("keydown", handleKeyDown);
            // Restore original function
            documentViewer.setCurrentPage = originalSetCurrentPage;
          });
        }

        // Listen for document loaded before setting up page listener
        documentViewer.addEventListener("documentLoaded", () => {
          logger("log", "Document loaded in DocumentViewerNew");
        });

        // Listen for page number updates and publish them
        const handlePageNumberUpdated = (pageNumber) => {
          // Don't broadcast if we're applying a remote change
          if (isApplyingRemoteChange.current) {
            return;
          }

          // Prevent page changes for non-moderators (unless it's a remote change)
          if (meetingRole !== "moderator") {
            // If non-moderator tries to change page manually, revert it
            if (!isApplyingRemoteChange.current) {
              // This should be prevented by pointerEvents: none, but just in case
              const currentPage = documentViewer.getCurrentPage();
              if (currentPage !== pageNumber) {
                isApplyingRemoteChange.current = true;
                documentViewer.setCurrentPage(currentPage);
                setTimeout(() => {
                  isApplyingRemoteChange.current = false;
                }, 100);
              }
            }
            return;
          }

          if (publishRef.current) {
            logger("log", "pageNumber Updated - broadcasting", pageNumber);
            publishRef.current(
              JSON.stringify({
                eventName: "pageNumberUpdated",
                eventData: { pageNumber: pageNumber },
              }),
              { persist: true }
            );
          }
        };

        documentViewer.addEventListener(
          "pageNumberUpdated",
          handlePageNumberUpdated
        );

        cleanupFunctions.push(() => {
          documentViewer.removeEventListener(
            "pageNumberUpdated",
            handlePageNumberUpdated
          );
        });

        webviewerInstanceRef.current = instance;
      });
    });

    // Cleanup function
    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
      cleanupFunctions = [];
    };
  }, [fileUrl, meetingRole]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {/* Close Button */}
      {meetingRole === "moderator" && (
        <button
          onClick={() => {
            if (onClosePanel && typeof onClosePanel === "function") {
              onClosePanel();
            }
          }}
          className="absolute top-[12px] right-[16px] z-[99999] bg-black/60 border-none rounded-full w-9 h-9 flex items-center justify-center text-white cursor-pointer hover:bg-black/80 transition-colors"
          aria-label="Close document viewer"
        >
          <FaTimes size={20} />
        </button>
      )}
      <div
        style={{
          width: "100%",
          height: "100%",
          pointerEvents: meetingRole !== "moderator" ? "none" : "auto",
          touchAction: meetingRole !== "moderator" ? "none" : "auto",
        }}
        ref={viewer}
      ></div>
    </div>
  );
}
